import BaseMap from 'components/BaseMap/BaseMap';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useGetBuoyDevicesQuery } from 'services/geography/buoys/buoy.api';
import MapLegendIcon from '../../DeviceMap/assets/MapLegendIcon.svg';
import DeviceMapLegend from '../../DeviceMap/features/components/DeviceMapLegend';
import BuoyMapLayer from '../components/BuoyMapLayer';
import { buoyDatapointToDevice } from '../utils/buoyDeviceUtils';

const DEFAULT_BUOY_WIDGET_ZOOM = 10;
const DEFAULT_SELECTED_BUOY_ZOOM = 12;
const BUOY_SOFT_MAX_BOUNDS = [
  [7, -171],
  [74, -50],
];

const getDistanceSquared = (pointA, pointB) => {
  if (!pointA || !pointB) return Number.POSITIVE_INFINITY;
  const latDiff = Number(pointA[0]) - Number(pointB[0]);
  const lngDiff = Number(pointA[1]) - Number(pointB[1]);
  return latDiff * latDiff + lngDiff * lngDiff;
};

const AllBuoysMap = ({ widget = false, selectedDeviceId = null }) => {
  const {
    data: buoyDeviceFetch = [],
    isLoading: buoyIsLoading,
    isFetching,
  } = useGetBuoyDevicesQuery();

  const buoyDevices = useMemo(
    () => buoyDeviceFetch.map((datapoint) => buoyDatapointToDevice(datapoint)),
    [buoyDeviceFetch, buoyIsLoading],
  );

  const { locations } = useLocation();
  const initialCenter = locations.workplaceLocation.coords;
  const [mapCenter, setMapCenter] = useState(initialCenter);
  const [mapZoom, setMapZoom] = useState(widget ? DEFAULT_BUOY_WIDGET_ZOOM : 6);
  const [mapBounds, setMapBounds] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const hasAutoCenteredWidget = useRef(false);

  const selectedBuoy = useMemo(
    () => buoyDevices.find((device) => String(device.id) === String(selectedDeviceId)) || null,
    [buoyDevices, selectedDeviceId],
  );

  useEffect(() => {
    if (!widget || !buoyDevices.length) return;

    if (selectedBuoy?.location?.lat != null && selectedBuoy?.location?.lng != null) {
      setMapCenter([selectedBuoy.location.lat, selectedBuoy.location.lng]);
      setMapZoom(DEFAULT_SELECTED_BUOY_ZOOM);
      hasAutoCenteredWidget.current = true;
      return;
    }

    if (hasAutoCenteredWidget.current) return;

    const nearestBuoy = buoyDevices.reduce((closest, device) => {
      const deviceCenter = [device?.location?.lat, device?.location?.lng];
      if (deviceCenter[0] == null || deviceCenter[1] == null) return closest;
      if (!closest) return device;

      return getDistanceSquared(deviceCenter, initialCenter) <
        getDistanceSquared([closest.location.lat, closest.location.lng], initialCenter)
        ? device
        : closest;
    }, null);

    if (nearestBuoy?.location?.lat != null && nearestBuoy?.location?.lng != null) {
      setMapCenter([nearestBuoy.location.lat, nearestBuoy.location.lng]);
      setMapZoom(DEFAULT_BUOY_WIDGET_ZOOM);
      hasAutoCenteredWidget.current = true;
    }
  }, [widget, buoyDevices, selectedBuoy, initialCenter]);

  return (
    <div
      className={`d-flex flex-column h-100 w-100 ${widget ? 'overflow-hidden rounded-3' : 'overflow-hidden'}`}
    >
      {!widget && <WidgetTimeUpdate route="getBuoyConditions" addMargin />}
      <div
        className={`flex-grow-1 min-h-0 ${widget ? 'rounded-3 overflow-hidden' : ''}`}
        style={{ position: 'relative', isolation: 'isolate' }}
      >
        <BaseMap
          mapCenter={mapCenter}
          zoom={mapZoom}
          setMapZoom={setMapZoom}
          bounds={mapBounds}
          setMapBounds={setMapBounds}
          maxZoom={widget ? 16 : 19}
          minZoom={widget ? 3 : 2}
          maxBounds={BUOY_SOFT_MAX_BOUNDS}
          maxBoundsViscosity={0.75}
          enableFilters={false}
        >
          <BuoyMapLayer
            showAllBuoyDevices={true}
            buoyPoints={buoyDevices}
            setMapCenter={setMapCenter}
            setMapZoom={setMapZoom}
          />
        </BaseMap>
        {buoyDevices.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              bottom: 8,
              zIndex: 3,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <div
              className="d-inline-flex flex-row align-items-end justify-content-end gap-0"
              style={{
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                overflow: 'visible',
                width: 'max-content',
                position: 'relative',
                pointerEvents: 'auto',
              }}
            >
              <div className="d-flex flex-column justify-content-end gap-1">
                <Button
                  variant="primary"
                  className="text-white p-0"
                  style={{ width: 40, height: 40 }}
                  onClick={() => setLegendOpen((prev) => !prev)}
                  title="Toggle map legend"
                >
                  <img
                    className="connected-devices-map-legend-icon"
                    src={MapLegendIcon}
                    alt="Map Legend"
                    height={25}
                    width={25}
                  />
                </Button>
              </div>
              {legendOpen && (
                <DeviceMapLegend
                  compact={widget}
                  setLegendToggle={setLegendOpen}
                  showBuoySection={true}
                  showUSGSSection={false}
                  style={{
                    height: '100%',
                    maxHeight: '100%',
                    maxWidth: widget ? 'min(300px, calc(100vw - 96px))' : 'min(360px, 100%)',
                    overflow: 'hidden',
                    width: widget ? 'min(300px, calc(100vw - 96px))' : 'min(360px, 100%)',
                    borderBottomRightRadius: 0,
                    borderTopRightRadius: 0,
                    marginLeft: 8,
                  }}
                />
              )}
            </div>
          </div>
        )}
        {(buoyIsLoading || isFetching) && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25 text-white z-1">
            Loading buoy conditions...
          </div>
        )}
        {!buoyIsLoading && !isFetching && buoyDevices.length === 0 && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white-50 bg-dark bg-opacity-25 z-1">
            No buoy locations available
          </div>
        )}
      </div>
    </div>
  );
};

export default AllBuoysMap;
